// Schedule's Week, Map, Who's working and Time off in Blueprint (pages/ux-full schedule/week,
// schedule/map, schedule/team, schedule/timeoff): ui-web's props, built from the real answers. Nothing
// here asks the server; the screens read dispatch.board, capacity.availability, staff.list and
// timeOff.list and hand them in.
import { addDays, instantAt, localDateOf, timeText, windowText } from '@flowology/kernel';
import type {
  DayMapProps,
  GoogleMapData,
  LabelKind,
  TeamListProps,
  TechDayProps,
  TimeOffListProps,
  VisitCardData,
  WeekGridProps,
} from '@flowology/ui-web';
import { plainText } from '../shared/plain-text.ts';
import { visitWord } from '@flowology/ui-web/words';
import {
  dayVisits,
  LATE_AFTER_MINUTES,
  overdueWords,
  windowText as visitWindow,
  daysFrom,
  lookWhy,
  overdue,
  overdueTone,
  shownStatus,
  WORK_DONE_WORDS,
  workDone,
  type Board,
  type BoardTech,
  type BoardVisit,
  type TechBlock,
  type TechDay,
} from '../board-model.ts';
import { penOf } from '../blueprint/schedule-model.ts';
import { stillBookedText } from '../board-extras-model.ts';

const ms = (iso: string) => Date.parse(iso);
const minutesOf = (a: string, b: string) => Math.max(0, Math.round((ms(b) - ms(a)) / 60_000));
/** "9 a.m.", "9:02 a.m." */
const clockAt = (iso: string, tz: string) => timeText(iso, tz).replace(/:00(?= )/, '');
const dayOf = (iso: string, tz: string) => localDateOf(iso, tz);

/** "Mon 26" */
export const weekDayLabel = (date: string) =>
  new Intl.DateTimeFormat('en-CA', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`))
    .replace(/,/g, '');

/** A technician's pen by their place among the technicians, as the day board gives it (by name). */
export function pensBy(technicians: readonly { id: string }[]): Map<string, number> {
  return new Map(technicians.map((t, i) => [t.id, penOf(i)]));
}

/** What takes them away all day, by name: an all-day time off or holiday (non-job time never is). */
const awayAllDay = (blocks: readonly TechBlock[] | undefined) =>
  blocks?.find((b) => b.allDay && b.kind !== 'non_job')?.label ?? null;

/**
 * A technician's hours that day in minutes, as the week weighs what's booked against them (QA-675):
 * the hours that apply (their own, their department's, else the company's); 0 when they don't work it.
 * Null when those hours cover the whole day, midnight to 23:59 (a company open round the clock): that
 * is no shift, so the week names no figure. The day's own length is the measure, so a day the clocks
 * change on is judged the same.
 */
export function shiftMinutes(day: TechDay | undefined, tz: string): number | null {
  const hours = day?.workingHours ?? [];
  if (!day || !hours.length) return 0;
  const minutes = hours.reduce((m, w) => m + minutesOf(w.start, w.end), 0);
  const dayLong = minutesOf(instantAt(day.date, '00:00', tz), instantAt(addDays(day.date, 1), '00:00', tz));
  // a window closes at 23:59 at the latest: a minute short of the day is all of it
  return minutes >= dayLong - 1 ? null : minutes;
}

export interface CapacityDay {
  date: string;
  techOpenMinutes?: { id: string; openMinutes: number }[];
}

/** The week by technician: dispatch.board's week, each day's free minutes from capacity.availability. */
export function weekGridProps(
  board: Board,
  capacity: readonly CapacityDay[] | null,
  today: string,
): WeekGridProps {
  const tz = board.timeZone;
  const dates = daysFrom(board.date, board.to ?? board.date);
  const open = new Map<string, number>();
  for (const d of capacity ?? [])
    for (const t of d.techOpenMinutes ?? []) open.set(`${t.id}:${d.date}`, t.openMinutes);
  const pens = pensBy(board.technicians);
  return {
    days: dates.map((date) => ({
      date,
      label: weekDayLabel(date),
      today: date === today,
      waiting: board.unassigned.filter((v) => dayOf(v.start, tz) === date).length,
    })),
    techs: board.technicians.map((t) => ({
      id: t.id,
      name: t.name,
      pen: pens.get(t.id) ?? null,
      cells: dates.map((date) => {
        const on = t.visits.filter((v) => dayOf(v.start, tz) === date);
        const day = t.days?.find((d) => d.date === date);
        return {
          date,
          visits: on.length,
          bookedMinutes: on.reduce((m, v) => m + minutesOf(v.start, v.end), 0),
          workingMinutes: shiftMinutes(day, tz),
          openMinutes: open.get(`${t.id}:${date}`) ?? null,
          away: awayAllDay(day?.blocks),
        };
      }),
    })),
  };
}

type TeamRow = TeamListProps['techs'][number];

/** A late or long visit's words for the lane, red: "Running long 20 min" (not one only as of a read). */
function lateWords(visits: readonly BoardVisit[], now: string, after: number | undefined, tz: string) {
  for (const v of visits) {
    const o = overdue(v, now, after);
    if (o && overdueTone(o) !== 'stale') {
      const why = lookWhy(o, tz);
      return why.charAt(0).toUpperCase() + why.slice(1);
    }
  }
  return null;
}

/**
 * Where a technician is today, from the board: on a job (the visit they're out on, on site), on the way
 * (it's dispatched), away (non-job time still going, or part-day time off now), off (no hours today, or
 * all-day time off or a holiday), else free, with what's next.
 */
export function teamRow(
  t: BoardTech,
  pen: number | null,
  skills: readonly string[],
  board: Pick<Board, 'timeZone' | 'lateAfterMinutes'>,
  date: string,
  now: string,
): TeamRow {
  const tz = board.timeZone;
  const visits = t.visits
    .filter((v) => dayOf(v.start, tz) === date)
    .sort((a, b) => ms(a.start) - ms(b.start));
  const day = t.days?.find((d) => d.date === date);
  const base = {
    id: t.id,
    name: t.name,
    pen,
    visits: visits.length,
    bookedMinutes: visits.reduce((m, v) => m + minutesOf(v.start, v.end), 0),
    skills: [...skills],
    // late is today's only: another day's visits aren't late yet
    late: date === dayOf(now, tz) ? lateWords(visits, now, board.lateAfterMinutes, tz) : null,
  };
  const offFor = awayAllDay(day?.blocks);
  if (offFor || (day && day.workingHours.length === 0))
    return { ...base, state: 'off', word: 'Off today', line: offFor ?? 'Not working today' };
  // not started: booked to them, or sent and not set off, whatever its time (QA-695)
  const own = (v: BoardVisit) => v.techs?.find((x) => x.id === t.id)?.fieldStatus;
  const unstarted = visits.filter((v) =>
    own(v) ? own(v) === 'assigned' || own(v) === 'dispatched' : v.status === 'scheduled',
  );
  // they ended the day (QA-694): off, and whatever is still booked to them is the office's to rebook
  if (t.clock?.out && date === dayOf(now, tz))
    return {
      ...base,
      state: 'off',
      word: DAY_ENDED_WORD,
      line: unstarted.length
        ? `Clocked out ${clockAt(t.clock.out, tz)}, ${stillBookedText(unstarted.length)} today: rebook ${unstarted.length === 1 ? 'it' : 'them'}`
        : `Clocked out ${clockAt(t.clock.out, tz)}`,
    };
  const active = visits.find((v) => v.appointmentId === t.activeAppointmentId);
  const mine = active ? (own(active) ?? active.status) : undefined;
  if (active && mine === 'working')
    return {
      ...base,
      state: 'working',
      word: 'On a job',
      line: `${active.customerName}, since ${clockAt(active.arrivedAt ?? active.start, tz)}`,
    };
  if (active && mine === 'dispatched' && !active.notStarted)
    return {
      ...base,
      state: 'driving',
      word: 'On the way',
      line: `To ${active.customerName}, ${windowText(active.arrivalWindow?.start ?? active.start, active.arrivalWindow?.end ?? active.end, tz)}`,
    };
  const away = day?.blocks.find(
    (b) => !b.allDay && ms(b.start) <= ms(now) && (b.end === null || ms(b.end) > ms(now)),
  );
  if (away)
    return {
      ...base,
      state: 'away',
      word: away.label,
      line: away.end ? `Until ${clockAt(away.end, tz)}` : `Since ${clockAt(away.start, tz)}`,
    };
  // the next not started, even one whose time has come: never "Nothing more booked" while one waits
  const next = unstarted[0];
  if (next && ms(next.start) <= ms(now))
    return {
      ...base,
      state: 'free',
      word: NOT_STARTED_WORD,
      line: `Due: ${next.customerName} at ${clockAt(next.start, tz)}, not started`,
    };
  return {
    ...base,
    state: 'free',
    word: 'Free',
    line: next ? `Next: ${next.customerName} at ${clockAt(next.start, tz)}` : 'Nothing more booked today',
  };
}

/** A tech whose booked visit's time has come and who hasn't started it (QA-695): not free. */
export const NOT_STARTED_WORD = 'Not started';

/** A technician who ended their day (QA-694). */
export const DAY_ENDED_WORD = 'Day ended';

/** The five figures over Who's working (the prototype's). */
export function teamFigures(rows: readonly TeamRow[]) {
  const n = (s: TeamRow['state']) => rows.filter((r) => r.state === s).length;
  return {
    working: n('working'),
    driving: n('driving'),
    // free right now: not one whose booked visit is due and not started (QA-695)
    free: rows.filter((r) => r.state === 'free' && r.word !== NOT_STARTED_WORD).length,
    late: rows.filter((r) => r.late).length,
    off: n('off'),
    // of the off ones, those who worked and ended their day (QA-835)
    ended: rows.filter((r) => r.state === 'off' && r.word === DAY_ENDED_WORD).length,
  };
}

/** Who's working's line: "3 of 4 technicians on today", the ones who ended their day said apart (QA-835). */
export function teamLede(rows: readonly TeamRow[], fig: ReturnType<typeof teamFigures>): string {
  const on = rows.length - fig.off;
  return fig.ended
    ? `${on} of ${rows.length} technicians on now, ${fig.ended} done for the day. ${fig.free} free right now.`
    : `${on} of ${rows.length} technicians on today. ${fig.free} free right now.`;
}

const OPEN_AT_LEAST = 45; // minutes: a shorter gap between visits isn't worth booking into

/**
 * One technician's day: their visits in order, time away (non-job time, part-day time off) as its own
 * stop, and the open gaps of 45 minutes or more inside their working hours.
 */
export function techDayProps(
  t: BoardTech,
  pen: number | null,
  tz: string,
  date: string,
  dateWords: string,
  now: string,
): TechDayProps {
  const day = t.days?.find((d) => d.date === date);
  const hours = day?.workingHours ?? [];
  const offFor = awayAllDay(day?.blocks);
  const base = {
    name: t.name,
    pen,
    date: dateWords,
    hours: hours.length ? hours.map((w) => windowText(w.start, w.end, tz)).join(', ') : null,
  };
  if (offFor || (day && !hours.length))
    return { ...base, bookedMinutes: 0, openMinutes: 0, off: offFor ?? 'Not working today', stops: [] };
  const visits = t.visits
    .filter((v) => dayOf(v.start, tz) === date)
    .sort((a, b) => ms(a.start) - ms(b.start));
  const away = (day?.blocks ?? []).filter((b) => !b.allDay);
  // QA-835: the technician's own state: done when closed or handed in with Done, paused when stopped
  // for now or handed to someone else, never Booked once they've been on it
  const state = (v: BoardVisit): 'done' | 'paused' | 'now' | 'next' => {
    const own = v.techs?.find((x) => x.id === t.id)?.fieldStatus ?? v.status;
    if (own === 'closed') return 'done';
    if (own === 'paused')
      return v.status === 'paused' && (v.pauseReason === 'work_done' || v.pauseReason === null)
        ? 'done'
        : 'paused';
    return own === 'working' || own === 'dispatched' ? 'now' : 'next';
  };
  const busy = [
    ...visits.map((v) => [ms(v.start), ms(v.end)] as const),
    ...away.map((b) => [ms(b.start), b.end ? ms(b.end) : ms(now)] as const),
  ].sort((a, b) => a[0] - b[0]);
  const gaps: { start: number; end: number }[] = [];
  for (const w of hours) {
    let at = Math.max(ms(w.start), date === dayOf(now, tz) ? ms(now) : 0); // today: from now on
    for (const [s, e] of busy) {
      if (e <= at || s >= ms(w.end)) continue;
      if (s - at >= OPEN_AT_LEAST * 60_000) gaps.push({ start: at, end: s });
      at = Math.max(at, e);
    }
    if (ms(w.end) - at >= OPEN_AT_LEAST * 60_000) gaps.push({ start: at, end: ms(w.end) });
  }
  const iso = (n: number) => new Date(n).toISOString();
  const today = date === dayOf(now, tz);
  type Stop = TechDayProps['stops'][number];
  const keyed: [number, Stop][] = [
    ...visits.map((v): [number, Stop] => [
      ms(v.start),
      {
        kind: 'visit',
        start: clockAt(v.start, tz),
        end: clockAt(v.end, tz),
        title: v.customerName,
        detail: [v.jobType?.name, v.address].filter(Boolean).join(', ') || null,
        state: state(v),
        late: today ? lateWords([v], now, undefined, tz) : null,
      },
    ]),
    ...away.map((b): [number, Stop] => [
      ms(b.start),
      {
        kind: 'away',
        start: clockAt(b.start, tz),
        end: b.end ? clockAt(b.end, tz) : 'now',
        title: b.label,
        detail: null,
        state: null,
        late: null,
      },
    ]),
    ...gaps.map((g): [number, Stop] => [
      g.start,
      {
        kind: 'open',
        start: clockAt(iso(g.start), tz),
        end: clockAt(iso(g.end), tz),
        title: 'Open',
        detail: null,
        state: null,
        late: null,
      },
    ]),
  ];
  return {
    ...base,
    bookedMinutes: visits.reduce((m, v) => m + minutesOf(v.start, v.end), 0),
    openMinutes: gaps.reduce((m, g) => m + Math.round((g.end - g.start) / 60_000), 0),
    off: null,
    stops: keyed.sort((a, b) => a[0] - b[0]).map(([, s]) => s),
  };
}

export interface TimeOffRow {
  id: string;
  staffId: string;
  staffName: string;
  label: string;
  paid: boolean;
  start: string;
  end: string;
  note: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  text: string;
}

/** timeOff.list's rows for TimeOffList: booked ones first, by when, then the cancelled; pens by board order. */
export function timeOffListProps(
  rows: readonly TimeOffRow[],
  pens: ReadonlyMap<string, number>,
): TimeOffListProps {
  const sorted = [...rows].sort(
    (a, b) => Number(!!a.cancelledAt) - Number(!!b.cancelledAt) || ms(a.start) - ms(b.start),
  );
  return {
    items: sorted.map((o) => ({
      id: o.id,
      name: o.staffName,
      pen: pens.get(o.staffId) ?? null,
      label: o.label,
      text: o.text,
      paid: o.paid,
      note: o.note,
      cancelled: o.cancelledAt ? { reason: o.cancelReason ?? 'No reason given' } : null,
    })),
  };
}

/** The week of a day, Monday to Sunday, in words: "October 19 – 25" or "October 26 – November 1". */
export function weekWords(from: string, to: string): string {
  const f = (d: string, o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-CA', { ...o, timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
  return `${f(from, { month: 'long', day: 'numeric' })} – ${f(to, from.slice(5, 7) === to.slice(5, 7) ? { day: 'numeric' } : { month: 'long', day: 'numeric' })}`;
}

// ── Map ──

/** A pin's state: done (closed, or handed in with the work done), now (on site), waiting (nobody on it yet), else booked. */
const pinState = (v: BoardVisit): DayMapProps['pins'][number]['state'] =>
  shownStatus(v) === 'closed'
    ? 'done'
    : v.status === 'working'
      ? 'now'
      : !v.techIds.length
        ? 'waiting'
        : 'booked';
const PIN_WORDS: Record<DayMapProps['pins'][number]['state'], string> = {
  done: 'done',
  now: 'on site',
  booked: 'booked',
  waiting: 'no one yet',
};

/** Late or running long now, as the board judges it (not one only as of a ServiceTitan read). */
const lateOf = (v: BoardVisit, now: string, after: number | undefined) => {
  const o = overdue(v, now, after ?? LATE_AFTER_MINUTES);
  return o && overdueTone(o) !== 'stale' ? o : null;
};

/** "Fairview" from "Fairview residence", "Smith" from "Smith, Jo": the pin's short label. */
const shortName = (name: string) =>
  name
    .trim()
    .split(/\s+/)[0]!
    .replace(/[,.;:]+$/, '') || name;

/** The town the board's address ends with ("1287 Fairview Rd, Saanich"). */
const townOf = (address: string) => {
  const parts = address.split(',').map((p) => p.trim());
  return parts.length > 1 ? parts[parts.length - 1]! || null : null;
};

/**
 * The day's visits on the map (dispatch.board's day, each visit once): a pin for each whose address has
 * a point, in its lead's pen; each technician's route through their visits in time order; the shop
 * where it has a point (none yet: Flowology keeps no address for it); and the rest listed, never dropped.
 */
export function dayMapProps(board: Board, pens: ReadonlyMap<string, number>, now: string): DayMapProps {
  const tz = board.timeZone;
  const today = board.date === dayOf(now, tz);
  const names = new Map(board.technicians.map((t) => [t.id, t.name]));
  const visits = dayVisits(board).sort((a, b) => ms(a.start) - ms(b.start));
  const lead = (v: BoardVisit) => v.leadTechId ?? v.techIds[0] ?? null;
  const who = (v: BoardVisit) => v.techIds.map((id) => names.get(id) ?? 'A technician').join(' and ');
  const pins: DayMapProps['pins'] = [];
  const unplaced: DayMapProps['unplaced'] = [];
  for (const v of visits) {
    const late = today ? lateOf(v, now, board.lateAfterMinutes) : null;
    const when = visitWindow(v, tz);
    if (!v.geo) {
      unplaced.push({
        id: v.appointmentId,
        title: v.customerName,
        line: `${when}, ${v.techIds.length ? who(v) : 'no one yet'}`,
        address: v.address,
      });
      continue;
    }
    const state = pinState(v);
    const l = lead(v);
    pins.push({
      id: v.appointmentId,
      lat: v.geo.lat,
      lng: v.geo.lng,
      label: shortName(v.customerName),
      title: [
        v.customerName,
        when,
        ...(v.techIds.length ? [who(v)] : []),
        PIN_WORDS[state],
        ...(late ? [lookWhy(late, tz)] : []),
      ].join(', '),
      area: townOf(v.address),
      state,
      late: !!late,
      pen: l ? (pens.get(l) ?? null) : null,
    });
  }
  return {
    pins,
    routes: board.technicians.map((t) => ({
      id: t.id,
      pen: pens.get(t.id) ?? null,
      stops: t.visits
        .filter((v) => v.geo)
        .sort((a, b) => ms(a.start) - ms(b.start))
        .map((v) => v.appointmentId),
    })),
    shop: null,
    unplaced,
  };
}

/**
 * The day's map on Google's map (BL-018, maps package 5): DayMap's pins, routes and shop as they are,
 * each pin's point the import's (dispatch.board's geo is the imported address's point; Flowology looks
 * up no address itself, so none is Google's and the stand-in draws them all), and each technician's dot
 * where they are. dispatch.board says nothing of where a technician is yet, so the board passes none.
 */
export function googleMapData(d: DayMapProps, techs: GoogleMapData['techs'] = []): GoogleMapData {
  return {
    pins: d.pins.map((p) => ({ ...p, source: 'imported' as const })),
    routes: d.routes,
    shop: d.shop,
    techs,
  };
}

/** The visit the map has open, as ui-web's VisitCard draws it: when, its state, who for, where and who. */
export function mapVisitCard(
  v: BoardVisit,
  board: Board,
  pens: ReadonlyMap<string, number>,
  now: string,
): VisitCardData {
  const tz = board.timeZone;
  const late = board.date === dayOf(now, tz) ? lateOf(v, now, board.lateAfterMinutes) : null;
  const kind: LabelKind = late
    ? 'bad'
    : v.status === 'working'
      ? 'now'
      : shownStatus(v) === 'paused'
        ? 'wait'
        : 'muted';
  const names = new Map(board.technicians.map((t) => [t.id, t.name]));
  return {
    when: visitWindow(v, tz),
    customer: v.customerName,
    address: v.address,
    work: plainText(v.summary),
    job: `#${v.jobNumber}`,
    // how the board says a visit's state: the app's one word for it (job-words)
    state: {
      label: workDone(v) ? WORK_DONE_WORDS : visitWord(v.status, { notStarted: !!v.notStarted }),
      kind,
    },
    techs: v.techIds.map((id) => ({ name: names.get(id) ?? 'A technician', pen: pens.get(id) ?? null })),
    notes: [
      ...(late ? [{ text: overdueWords(late, tz).line, kind: 'bad' as const }] : []),
      ...(v.geo ? [] : [{ text: 'Not on the map: no location for this address.' }]),
    ],
  };
}
